import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type PermissionState = "default" | "granted" | "denied";

export const useBrowserNotifications = () => {
  const { user } = useAuth();
  const [permission, setPermission] = useState<PermissionState>(
    typeof Notification !== "undefined" ? Notification.permission as PermissionState : "denied"
  );
  const profileCacheRef = useRef<Record<string, string>>({});

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setPermission(result as PermissionState);
  }, []);

  const resolveSenderName = useCallback(async (senderId: string): Promise<string> => {
    if (profileCacheRef.current[senderId]) return profileCacheRef.current[senderId];
    const { data } = await supabase
      .from("profiles")
      .select("first_name, last_name")
      .eq("user_id", senderId)
      .single();
    const name = data
      ? `${data.first_name || ""} ${data.last_name || ""}`.trim() || "Your Care Team"
      : "Your Care Team";
    profileCacheRef.current[senderId] = name;
    return name;
  }, []);

  // Message notifications (for all users)
  useEffect(() => {
    if (!user || permission !== "granted") return;

    const channel = supabase
      .channel("push-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        async (payload) => {
          const msg = payload.new as {
            sender_id: string;
            recipient_id: string;
            body: string;
            subject: string | null;
          };

          if (msg.recipient_id !== user.id) return;
          if (document.hasFocus()) return;

          const senderName = await resolveSenderName(msg.sender_id);
          const title = `New message from ${senderName}`;
          const body = msg.body.length > 100 ? msg.body.slice(0, 97) + "…" : msg.body;

          new Notification(title, {
            body,
            icon: "/placeholder.svg",
            tag: "elara-message",
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, permission, resolveSenderName]);

  return {
    permission,
    requestPermission,
    isSupported: typeof Notification !== "undefined",
  };
};

/**
 * Hook specifically for admin action-queue push notifications.
 * Listens to realtime inserts on high-priority tables and fires
 * browser notifications when the tab is not focused.
 */
export const useAdminActionQueueNotifications = () => {
  const { user } = useAuth();
  const [permission, setPermission] = useState<PermissionState>(
    typeof Notification !== "undefined" ? Notification.permission as PermissionState : "denied"
  );

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setPermission(result as PermissionState);
  }, []);

  useEffect(() => {
    if (!user || permission !== "granted") return;

    const channel = supabase
      .channel("admin-action-push")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "role_requests" },
        (payload) => {
          if (document.hasFocus()) return;
          const r = payload.new as any;
          if (r.status !== "pending") return;
          const meta = r.metadata as Record<string, any> | null;
          const name = [meta?.first_name, meta?.last_name].filter(Boolean).join(" ") || "Someone";
          new Notification("🔴 New Role Request", {
            body: `${name} has requested the ${r.requested_role} role and needs your review.`,
            icon: "/placeholder.svg",
            tag: `action-role-${r.id}`,
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "provider_credentials" },
        (payload) => {
          if (document.hasFocus()) return;
          const cred = payload.new as any;
          if (cred.credential_status !== "pending_review") return;
          new Notification("🔴 Provider Ready for Review", {
            body: "A provider has completed onboarding and needs credential review.",
            icon: "/placeholder.svg",
            tag: `action-cred-${cred.id}`,
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "qa_issues" },
        (payload) => {
          if (document.hasFocus()) return;
          const qa = payload.new as any;
          if (qa.severity !== "critical") return;
          new Notification("🔴 Critical QA Issue", {
            body: qa.title || "A critical quality issue has been reported.",
            icon: "/placeholder.svg",
            tag: `action-qa-${qa.id}`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, permission]);

  return { permission, requestPermission, isSupported: typeof Notification !== "undefined" };
};
